/* mm-feature-stepper: "At a glance" photo crossfade + numbered steps (sections/mm-feature-stepper.liquid).
   The active step's progress bar is the clock: its CSS animation runs for the interval and `animationend` moves to the
   next step (the design advances every 5s). Hovering the photo or the list with a mouse, keyboard focus inside, or a
   step block selected in the theme editor holds the current step (`data-held`: the bar shows full, as in the design);
   leaving restarts the bar. Off-screen (< 20% visible) or a hidden tab pauses the bar where it is. No auto-advance with
   reduced motion. Mouse hover on a row, a click on a row, or a dot on the photo shows that step. A custom element, so
   it re-initialises itself when the theme editor re-renders the section. */
(() => {
  if (customElements.get('mm-feature-stepper')) return;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  customElements.define('mm-feature-stepper', class extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.frames = [...this.querySelectorAll('[data-frame]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.pill = this.querySelector('[data-pill]');
      if (!this.steps.length) return;
      this.ac = new AbortController();
      const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.ac.signal });
      this.state = { hover: 0, focus: false, inView: false, hold: false };

      this.steps.forEach((step, i) => {
        on(step, 'click', () => { if (this.index !== i) this.go(i); });
        on(step, 'pointerenter', (e) => { if (e.pointerType === 'mouse' && this.index !== i) this.go(i); });
        const fill = step.querySelector('[data-fill]');
        if (fill) on(fill, 'animationend', () => { if (this.running && this.index === i) this.go((i + 1) % this.steps.length); });
      });
      this.dots.forEach((dot, i) => on(dot, 'click', () => { if (this.index !== i) this.go(i); }));
      this.querySelectorAll('[data-pause]').forEach((zone) => {
        on(zone, 'pointerenter', (e) => { if (e.pointerType === 'mouse') this.set('hover', this.state.hover + 1); });
        on(zone, 'pointerleave', (e) => { if (e.pointerType === 'mouse') this.set('hover', Math.max(0, this.state.hover - 1)); });
      });
      // Keyboard focus holds; a mouse click on a step does not leave it held once the pointer has gone.
      on(this, 'focusin', (e) => { if (e.target.matches?.(':focus-visible')) this.set('focus', true); });
      on(this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) this.set('focus', false); });
      on(document, 'visibilitychange', () => this.sync());
      on(still, 'change', () => this.sync());
      // Theme editor: selecting a step block shows it and holds there until it is deselected.
      on(document, 'shopify:block:select', (e) => {
        const i = this.steps.indexOf(e.target.closest?.('[data-step]'));
        if (i < 0) return;
        this.state.hold = true;
        this.go(i);
      });
      on(document, 'shopify:block:deselect', (e) => { if (this.steps.includes(e.target.closest?.('[data-step]'))) this.set('hold', false); });

      this.io = new IntersectionObserver(([entry]) => this.set('inView', entry.isIntersecting), { threshold: 0.2 });
      this.io.observe(this);
      this.go(Math.max(0, this.steps.findIndex((s) => s.classList.contains('is-active'))));
    }

    disconnectedCallback() {
      this.ac?.abort();
      this.io?.disconnect();
    }

    set(key, value) {
      this.state[key] = value;
      this.sync();
    }

    go(i) {
      this.index = i;
      const mark = (list, attr) => list.forEach((el, n) => {
        el.classList.toggle('is-active', n === i);
        if (attr) n === i ? el.setAttribute(attr[0], attr[1]) : el.removeAttribute(attr[0]);
      });
      mark(this.steps);
      this.steps.forEach((step, n) => {
        const trigger = step.querySelector('[data-trigger]');
        if (n === i) trigger?.setAttribute('aria-current', 'step');
        else trigger?.removeAttribute('aria-current');
      });
      mark(this.dots, ['aria-current', 'step']);
      this.frames.forEach((frame, n) => {
        frame.classList.toggle('is-active', n === i);
        if (n === i) frame.removeAttribute('aria-hidden');
        else frame.setAttribute('aria-hidden', 'true');
      });
      const step = this.steps[i];
      if (this.pill) this.pill.textContent = `${step.dataset.label} · ${step.dataset.title}`;
      // Restart the active bar from zero.
      const fill = step.querySelector('[data-fill]');
      if (fill) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }
      this.sync();
    }

    sync() {
      const ticking = this.hasAttribute('data-auto') && !still.matches && this.steps.length > 1;
      const { hover, focus, inView, hold } = this.state;
      const held = hover > 0 || focus || hold;
      this.running = ticking && inView && !held && !document.hidden;
      this.toggleAttribute('data-ticking', ticking);
      this.toggleAttribute('data-held', ticking && held);
      this.toggleAttribute('data-running', this.running);
    }
  });
})();
